import { defineStore } from 'pinia';

import { stepConfigs } from '@bv/components/workflow/partials/steps';
import { buildWebhookUrl } from '@bv/components/workflow/partials/steps/incoming-webhook';

import {
  Flow,
  FlowNode,
  ServiceType,
  NodeCategory,
  ConnectorType,
  TriggerType,
  FlowNodeSnapshot,
} from '../types/workflow';
import { normalizeAskQuestionForApi, normalizeAskQuestionForUI } from '../utils/ask-question-normalize';
import { normalizeGoogleSheetsForApi, normalizeGoogleSheetsForUI } from '../utils/google-sheets-normalize';
import { getClonedFieldsForNodeType } from '../utils/node-validation';

import { useWorkflowStore } from './workflow';
import { useWorkflowValidation } from './workflow-validation';
import { useWorkflowVariables } from './workflow-variables';

const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const cloneData = (data: Record<string, any> = {}) => JSON.parse(JSON.stringify(data || {}));

export const useWorkflowGraph = defineStore('bvWorkflowGraph', {
  state: () => ({
    service: 'bbc' as ServiceType,
    MAX_STEPS: 100 as const,
    notSupportedNodeTypes: [] as string[],
    flow: {
      __root__: {
        id: '__root__',
        parentId: null,
        name: 'Trigger',
        category: 'Trigger',
        type: 'Trigger',
        children: [],
        data: {
          logicalOperator: 'and',
          triggerType: 'ConversationOpened',
        },
        triggerType: 'ConversationOpened',
      },
    } as Flow,
    confirmationDialog: {
      isOpen: false,
      title: 'Confirm Deletion',
      message: 'There are existing steps under this branch,',
      nodeName: '',
      nodeId: null as string | number | null,
      onConfirm: null as (() => void) | null,
    },
  }),

  getters: {
    // Get all nodes of a specific category
    getNodesByCategory: (state) => (category: NodeCategory) => {
      return Object.values(state.flow).filter((node) => node.category === category);
    },

    // Check if a node can be deleted
    canDeleteNode: (state) => (nodeId: string | number) => {
      const node = state.flow[nodeId];
      return node;
    },

    // Get total number of steps (excluding connectors)
    totalSteps: (state) => {
      return Object.values(state.flow).filter((node) => node.category !== 'Connector').length;
    },

    // Check if a node type is not supported (premium)
    isNodeTypeNotSupported: (state) => (type: string) => {
      return state.notSupportedNodeTypes.includes(type);
    },

    getFlow: (state) => {
      return Object.values(state.flow).reduce(
        (acc, node) => {
          let processedNode = {
            ...node,
            // Always capitalize types for consistency
            type: node.type.charAt(0).toUpperCase() + node.type.slice(1),
          };

          // Handle special cases for different node types
          if (node.id === '__root__') {
            // Handle __root__ node - use triggerType as type
            let rootData = processedNode.data;

            if (processedNode.triggerType === 'Webhook' && rootData) {
              const { webhookUrl: _webhookUrl, ...webhookData } = rootData;
              rootData = webhookData;
            }

            processedNode = {
              ...processedNode,
              type: processedNode.triggerType || '',
              data: rootData,
            };
          } else if (processedNode.type === 'Branch') {
            // Handle branch nodes - ensure data is empty object
            processedNode = {
              ...processedNode,
              data: {},
            };
          } else if (processedNode.type === 'AskQuestion') {
            processedNode = normalizeAskQuestionForApi(processedNode as FlowNode);
          } else if (processedNode.type === 'AddGoogleSheetRow') {
            processedNode = normalizeGoogleSheetsForApi(processedNode as FlowNode);
          }

          acc[node.id] = processedNode;
          return acc;
        },
        {} as Record<string, FlowNode>,
      );
    },

    getLastCreatedNode: (state) => {
      const nodes = Object.values(state.flow);

      if (nodes.length === 0) {
        return null;
      }

      // Sort nodes by the timestamp extracted from the ID
      const sorted = nodes.sort((a, b) => {
        // Handle special case for __root__ (treat it as oldest)
        if (a.id === '__root__') {
          return 1;
        }
        if (b.id === '__root__') {
          return -1;
        }

        // Extract timestamps (e.g., node_1764569249853_... -> 1764569249853)
        const getTimestamp = (id: any) => parseInt(id.split('_')[1] || 0);

        return getTimestamp(b.id) - getTimestamp(a.id); // Descending order
      });

      // Return the first one (the newest)
      const newest = sorted[0];
      if (newest.category === 'Connector') {
        return nodes.find((node) => node.id === newest.parentId);
      } else {
        return newest;
      }
    },
  },

  actions: {
    // Finds which branch a node belongs to by walking up to the connector or root
    getBranchRootId(nodeId: string | number | null) {
      if (!nodeId) {
        return null;
      }

      let current: FlowNode | null = this.flow[nodeId] ?? null;
      if (!current) {
        return '__root__';
      }

      while (current) {
        // Connector nodes mark the start of a branch
        if (current.category === 'Connector') {
          return current.id;
        }

        // Root-level nodes have no parent
        if (current.parentId === null) {
          return current.id;
        }

        // Get the parent node
        const parentId: string | number = current.parentId;
        let parent: FlowNode | null = this.flow[parentId] ?? null;

        // Handle orphaned parentId references (parent doesn't exist in flow)
        if (!parent) {
          parent = this.findActualParent(current.id);
          if (parent) {
            // Fix the orphaned reference for future calls
            current.parentId = parent.id;
            current = parent;
            continue;
          }
          // Node is orphaned - treat as root level
          return '__root__';
        }

        current = parent;
      }

      return '__root__';
    },

    // Helper: Find the actual parent of a node by searching for nodes that have it as a child
    findActualParent(nodeId: string | number): FlowNode | null {
      return Object.values(this.flow).find((node) => node.children && node.children.includes(nodeId)) ?? null;
    },

    // Makes a deep copy of a node and all its children for copying
    buildNodeSnapshot(nodeId: string | number): FlowNodeSnapshot {
      const node = this.flow[nodeId];
      if (!node) {
        throw new Error('Node not found');
      }

      const snapshot: FlowNodeSnapshot = {
        name: node.name,
        category: node.category,
        type: node.type,
        data: cloneData(node.data),
        triggerType: node.triggerType,
        connectorType: node.connectorType,
        children: [],
      };

      node.children.forEach((childId) => {
        if (this.flow[childId]) {
          snapshot.children.push(this.buildNodeSnapshot(childId));
        }
      });

      return snapshot;
    },

    // Counts how many steps are in a snapshot (connectors don't count)
    countSnapshotSteps(snapshot: FlowNodeSnapshot): number {
      let count = snapshot.category === 'Connector' ? 0 : 1;
      snapshot.children.forEach((child) => {
        count += this.countSnapshotSteps(child);
      });
      return count;
    },

    // Creates new nodes from a snapshot with fresh IDs
    // mode: 'single' = just the node + connectors, 'full' = everything including sub-steps
    instantiateSnapshot(
      snapshot: FlowNodeSnapshot,
      parentId: string | number,
      mode: 'single' | 'connectorsOnly' | 'full' = 'full',
    ): {
      rootId: string | number;
      leafNodeIds: Array<string | number>;
      firstConnectorId: string | number | null;
      createdNodeIds: Array<string | number>;
    } {
      const id = generateId();
      const newNode: FlowNode = {
        id,
        parentId,
        name: snapshot.name,
        category: snapshot.category,
        type: snapshot.type,
        children: [],
        data: cloneData(snapshot.data),
        triggerType: snapshot.triggerType,
        connectorType: snapshot.connectorType,
      };

      this.flow[id] = newNode;

      let leafNodeIds: Array<string | number> = [];
      let firstConnectorId: string | number | null = null;
      let createdNodeIds: Array<string | number> = [id];

      const childSnapshots =
        mode === 'single'
          ? snapshot.children.filter((child) => child.category === 'Connector')
          : mode === 'connectorsOnly'
            ? []
            : snapshot.children;

      childSnapshots.forEach((childSnapshot) => {
        const childMode = mode === 'full' ? 'full' : 'connectorsOnly';
        const childResult = this.instantiateSnapshot(childSnapshot, id, childMode);
        newNode.children.push(childResult.rootId);
        leafNodeIds = leafNodeIds.concat(childResult.leafNodeIds);
        createdNodeIds = createdNodeIds.concat(childResult.createdNodeIds);
        if (firstConnectorId === null && this.flow[childResult.rootId]?.category === 'Connector') {
          firstConnectorId = childResult.rootId;
        }
      });

      if (childSnapshots.length === 0) {
        leafNodeIds.push(id);
      }

      if (mode === 'single' && newNode.children.length === 0) {
        leafNodeIds = [id];
      }

      if (newNode.category === 'Router' && firstConnectorId === null) {
        const foundConnector = newNode.children.find((childId) => this.flow[childId]?.category === 'Connector');
        firstConnectorId = foundConnector ?? null;
      }

      return {
        rootId: id,
        leafNodeIds,
        firstConnectorId,
        createdNodeIds,
      };
    },

    attachExistingChildren(existingChildren: Array<string | number>, targetNodeId: string | number | null) {
      if (!targetNodeId || existingChildren.length === 0) {
        return;
      }
      const targetNode = this.flow[targetNodeId];
      if (!targetNode) {
        return;
      }
      targetNode.children = [...targetNode.children, ...existingChildren];
      existingChildren.forEach((childId) => {
        if (this.flow[childId]) {
          this.flow[childId].parentId = targetNodeId;
        }
      });
    },

    pickContinuationNode(leafNodeIds: Array<string | number>, fallbackId: string | number) {
      const nonConnectorLeaf = leafNodeIds.find((leafId) => this.flow[leafId]?.category !== 'Connector');
      if (nonConnectorLeaf) {
        return nonConnectorLeaf;
      }
      return leafNodeIds[0] ?? fallbackId;
    },

    // Find the leaf node starting from a given node ID
    findLeafNodeInBranch(startNodeId: string | number): string | number | null {
      const startNode = this.flow[startNodeId];
      if (!startNode) {
        return null;
      }

      // If this node has no children, it's a leaf
      if (startNode.children.length === 0) {
        return startNodeId;
      }

      // For nodes with children, we need to find the actual leaf node
      // by following the chain until we reach a node with no children
      let currentNode = startNode;
      while (currentNode.children.length > 0) {
        // For connectors or routers, follow the first child (main route)
        // For regular nodes, follow the first child
        const nextNodeId = currentNode.children[0];
        const nextNode = this.flow[nextNodeId];
        if (!nextNode) {
          break;
        }
        currentNode = nextNode;
      }

      return currentNode.id;
    },

    setService(service: ServiceType) {
      this.service = service;
    },

    setNotSupportedNodeTypes(types: string[]) {
      this.notSupportedNodeTypes = types;
    },

    /**
     * Add a new node to the flow graph
     */
    addNode(newNode: Partial<FlowNode> & { type: string; category: NodeCategory }, addClickedNode: FlowNode) {
      const store = useWorkflowStore();
      if (!addClickedNode) {
        return;
      }

      // Check if adding this step would exceed the maximum limit
      if (newNode.category !== 'Connector' && this.totalSteps >= this.MAX_STEPS) {
        return;
      }

      const id = generateId();
      store.startHistory();
      store.addToHistory(addClickedNode.id);
      store.addToHistory(id);
      const node: FlowNode = {
        id,
        parentId: addClickedNode.id,
        name: newNode.name || newNode.type,
        category: newNode.category,
        type: newNode.type,
        children: [],
        data: newNode.data || {},
        connectorType: newNode.connectorType,
      };

      // Auto-number steps (excluding connectors)
      if (newNode.category !== 'Connector') {
        const stepNumber = this.getNextStepNumber(newNode.type);
        node.name = `${newNode.name || newNode.type} #${stepNumber}`;
      }

      const parent = this.flow[addClickedNode.id];

      // Handle different node categories
      if (newNode.category === 'Router') {
        // For router nodes, we need to create connector nodes
        this.addRouterNodeWithConnectors(node);
        // Insert router node between parent and its children
        this.insertRouterNodeBetweenParentAndChildren(node, parent);
      } else {
        // For regular nodes, insert between parent and children
        this.insertNodeBetweenParentAndChildren(node, parent);
      }

      // Add the new node to the flow
      this.flow[id] = node;
      this.setDefaultValues(id);

      // Validate the newly added node to ensure errors are tracked immediately
      const clonedFields = getClonedFieldsForNodeType(node.type);
      if (clonedFields) {
        // Use setTimeout to ensure validation happens after node is fully added
        setTimeout(() => {
          store.validateNode(id, clonedFields, this.flow[id].data);
        }, 0);
      }

      store.endHistory();

      return id;
    },

    /**
     * Add a router node with default connector nodes (e.g., ValidAnswer/InvalidAnswer for askQuestion)
     */
    addRouterNodeWithConnectors(routerNode: FlowNode) {
      // Get the step configuration to determine connectors
      const stepConfig = stepConfigs[routerNode.type];
      if (!stepConfig || !stepConfig.connectors) {
        // Fallback to default connectors if no configuration is found
        const connectors = this.getDefaultConnectorsForRouterNode(routerNode.type);
        this.createConnectorsFromConfig(routerNode, connectors);
        return;
      }

      // Create connectors based on the step configuration
      this.createConnectorsFromConfig(routerNode, stepConfig.connectors);
    },

    /**
     * Create connector nodes from configuration
     */
    createConnectorsFromConfig(routerNode: FlowNode, connectorConfigs: Record<string, any>) {
      const store = useWorkflowStore();
      const connectorIds: (string | number)[] = [];

      // Create connector nodes from the record structure
      Object.entries(connectorConfigs).forEach(([connectorKey, connectorConfig]) => {
        // Check if connector should be shown based on show property
        const shouldShow = connectorConfig.show;

        // If show is a function, check it; otherwise use the boolean value or default to true
        const showConnector =
          typeof shouldShow === 'function' ? shouldShow(routerNode.data || {}) : shouldShow !== false;

        if (!showConnector) {
          return;
        }

        const connectorId = generateId();
        const connectorNode: FlowNode = {
          id: connectorId,
          parentId: routerNode.id,
          name: connectorConfig.title || connectorKey,
          category: 'Connector',
          type: connectorKey, // Use the key as the type
          children: [],
          data: {},
          connectorType: connectorKey as ConnectorType, // Use the key as connectorType
        };
        store.addToHistory(connectorId);
        connectorIds.push(connectorId);
        this.flow[connectorId] = connectorNode;
      });

      // Set router node children to all connector nodes
      routerNode.children = connectorIds;
    },

    /**
     * Get default connectors for a router node type
     */
    getDefaultConnectorsForRouterNode(routerNodeType: string): Array<{
      name: string;
      type: string;
      connectorType: string;
    }> {
      switch (routerNodeType) {
        case 'AskQuestion':
          return [
            { name: 'Valid Answer', type: 'ValidAnswer', connectorType: 'ValidAnswer' },
            { name: 'Invalid Answer', type: 'InvalidAnswer', connectorType: 'InvalidAnswer' },
          ];
        case 'BranchStep':
          return [
            { name: 'Condition 1', type: 'Condition1', connectorType: 'Success' },
            { name: 'Condition 2', type: 'Condition2', connectorType: 'Failure' },
            { name: 'Default', type: 'Default', connectorType: 'Default' },
          ];
        default:
          // For any future router node types, provide default success/failure connectors
          return [
            { name: 'Success', type: 'Success', connectorType: 'Success' },
            { name: 'Failure', type: 'Failure', connectorType: 'Failure' },
          ];
      }
    },

    /**
     * Insert a node between parent and its children
     */
    insertNodeBetweenParentAndChildren(node: FlowNode, parent: FlowNode) {
      if (parent.children.length > 0) {
        // Move all current children to the new node
        node.children = [...parent.children];
        // Update parentId of moved children
        node.children.forEach((childId) => {
          if (this.flow[childId]) {
            this.flow[childId].parentId = node.id;
          }
        });
        // Parent now only has the new node as its child
        parent.children = [node.id];
      } else {
        // Just add as a new child
        parent.children.push(node.id);
      }
    },

    /**
     * Insert a router node between parent and its children, moving children to the first connector
     */
    insertRouterNodeBetweenParentAndChildren(routerNode: FlowNode, parent: FlowNode) {
      if (parent.children.length > 0) {
        // Move all current children to the first connector of the router node
        const firstConnectorId = routerNode.children[0];
        if (firstConnectorId && this.flow[firstConnectorId]) {
          const firstConnector = this.flow[firstConnectorId];
          firstConnector.children = [...parent.children];
          // Update parentId of moved children to point to the first connector
          firstConnector.children.forEach((childId) => {
            if (this.flow[childId]) {
              this.flow[childId].parentId = firstConnectorId;
            }
          });
        }
        // Parent now only has the router node as its child
        parent.children = [routerNode.id];
      } else {
        // Just add as a new child
        parent.children.push(routerNode.id);
      }
    },

    /**
     * Show confirmation dialog for node deletion
     */
    showDeleteConfirmation(nodeId: string | number) {
      const node = this.flow[nodeId];
      if (!node) {
        return;
      }

      this.confirmationDialog = {
        isOpen: true,
        title: 'Confirm Deletion',
        message: 'There are existing steps under this branch,',
        nodeName: node.name,
        nodeId,
        onConfirm: () => this.performNodeDeletion(nodeId),
      };
    },

    /**
     * Handle confirmation dialog confirm action
     */
    confirmDeletion() {
      if (this.confirmationDialog.onConfirm) {
        this.confirmationDialog.onConfirm();
      }
      this.closeConfirmationDialog();
    },

    /**
     * Handle confirmation dialog cancel action
     */
    cancelDeletion() {
      this.closeConfirmationDialog();
    },

    /**
     * Close confirmation dialog
     */
    closeConfirmationDialog() {
      this.confirmationDialog = {
        isOpen: false,
        title: 'Confirm Deletion',
        message: 'There are existing steps under this branch,',
        nodeName: '',
        nodeId: null,
        onConfirm: null,
      };
    },

    /**
     * Recursively clear validation errors for a node and all its descendants
     */
    clearNodeErrorsRecursively(nodeId: string | number) {
      const validationStore = useWorkflowValidation();
      const node = this.flow[nodeId];
      if (!node) {
        return;
      }

      // Clear errors for this node
      validationStore.clearNodeErrors(nodeId);

      // Recursively clear errors for all children
      node.children.forEach((childId) => {
        this.clearNodeErrorsRecursively(childId);
      });
    },

    /**
     * Perform the actual node deletion (internal method)
     */
    performNodeDeletion(nodeId: string | number) {
      const node = this.flow[nodeId];
      if (!node) {
        return;
      }

      // Clear Jump step references to this node before deletion
      this.clearJumpReferences(nodeId);

      // Special handling for router nodes - need to clean up all connected workflow steps
      if (node.category === 'Router') {
        this.deleteRouterNodeAndAllConnections(nodeId);
        return;
      }

      // Remove from parent's children
      if (node.parentId !== null && this.flow[node.parentId]) {
        this.flow[node.parentId].children = this.flow[node.parentId].children.filter((id) => id !== nodeId);
      }

      // Recursively clear errors for this node and all its descendants before deletion
      this.clearNodeErrorsRecursively(nodeId);

      // Recursively delete children (skip confirmation for children)
      node.children.forEach((childId) => {
        this.deleteNode(childId, true, true);
      });

      // Delete node
      delete this.flow[nodeId];
    },

    /**
     * Check if a node has workflow steps that would be deleted
     */
    hasWorkflowStepsToDelete(nodeId: string | number): boolean {
      const node = this.flow[nodeId];
      if (!node || node.children.length === 0) {
        return false;
      }

      // For Router nodes, check if any connector has workflow steps
      if (node.category === 'Router') {
        return node.children.some((connectorId) => {
          const connector = this.flow[connectorId];
          return connector && connector.children.length > 0;
        });
      }

      // For regular nodes, check if they have direct workflow steps
      return node.children.some((childId) => this.flow[childId]);
    },

    /**
     * Delete a node and its descendants from the flow
     */
    deleteNode(nodeId: string | number, skipConfirmation = false, skipHistory = false) {
      const store = useWorkflowStore();
      const node = this.flow[nodeId];
      if (!node) {
        return;
      }

      // Check if node can be deleted
      if (!this.canDeleteNode(nodeId)) {
        return;
      }

      // Show confirmation dialog if node has workflow steps to delete (only at top level)
      if (!skipConfirmation && this.hasWorkflowStepsToDelete(nodeId)) {
        this.showDeleteConfirmation(nodeId);
        return;
      }

      if (!skipHistory) {
        store.startHistory();
      }

      if (node.parentId && this.flow[node.parentId]) {
        store.addToHistory(node.parentId);
      }
      store.addToHistory(nodeId);

      // If no workflow steps to delete or confirmation skipped, proceed with deletion
      this.performNodeDeletion(nodeId);

      // open side panel with last node
      store.selectNode(this.getLastCreatedNode!.id);

      if (!skipHistory) {
        store.endHistory();
      }
    },

    /**
     * Special deletion method for router nodes that ensures all connected workflow steps are removed
     */
    deleteRouterNodeAndAllConnections(routerNodeId: string | number) {
      const store = useWorkflowStore();
      const validationStore = useWorkflowValidation();
      const routerNode = this.flow[routerNodeId];
      if (!routerNode || routerNode.category !== 'Router') {
        return;
      }

      // Clear Jump references to the router node itself
      this.clearJumpReferences(routerNodeId);

      // Remove router node from its parent's children
      if (routerNode.parentId !== null && this.flow[routerNode.parentId]) {
        this.flow[routerNode.parentId].children = this.flow[routerNode.parentId].children.filter(
          (id) => id !== routerNodeId,
        );
      }

      // For each connector node, recursively delete all its descendants (the actual workflow steps)
      routerNode.children.forEach((connectorId) => {
        store.addToHistory(connectorId);
        if (this.flow[connectorId]) {
          const connectorNode = this.flow[connectorId];
          // Recursively clear errors for all workflow steps connected to this connector
          connectorNode.children.forEach((workflowStepId) => {
            if (this.flow[workflowStepId]) {
              this.clearNodeErrorsRecursively(workflowStepId);
            }
          });
          // Recursively delete all workflow steps connected to this connector (skip confirmation)
          connectorNode.children.forEach((workflowStepId) => {
            if (this.flow[workflowStepId]) {
              this.deleteNode(workflowStepId, true, true);
            }
          });
          // Clear errors for connector node before deletion
          validationStore.clearNodeErrors(connectorId);
          // Delete the connector node itself
          delete this.flow[connectorId];
        }
      });

      // Clear validation errors for router node before deletion
      validationStore.clearNodeErrors(routerNodeId);

      // Finally delete the router node
      delete this.flow[routerNodeId];
    },

    /**
     * Update node data (e.g., form values)
     */
    updateNodeData(nodeId: string | number, data: Record<string, any>) {
      if (this.flow[nodeId]) {
        const store = useWorkflowStore();
        // Merge with existing data to preserve other fields
        this.flow[nodeId].data = {
          ...this.flow[nodeId].data,
          ...data,
        };

        store.replaceNodeDataInHistory(nodeId, this.flow[nodeId].data, 'data');
      }
    },

    /**
     * Update node trigger type (for trigger nodes)
     */
    updateNodeTriggerType(nodeId: string | number, triggerType: TriggerType) {
      if (this.flow[nodeId] && this.flow[nodeId].category === 'Trigger') {
        const store = useWorkflowStore();
        this.flow[nodeId].triggerType = triggerType;
        store.replaceNodeDataInHistory(nodeId, triggerType, 'trigger');
      }
    },

    updateNodeName(nodeId: string | number, newName: string) {
      if (this.flow[nodeId]) {
        const store = useWorkflowStore();
        this.flow[nodeId].name = newName;
        store.replaceNodeDataInHistory(nodeId, newName, 'name');
      }
    },

    /**
     * Set the entire flow and extract variables
     */
    setFlow(flow: Flow) {
      const store = useWorkflowStore();
      const recievedFlow = { ...flow };

      if (recievedFlow.__root__?.type === 'Webhook' && recievedFlow.__root__.data?.webhookToken) {
        recievedFlow.__root__ = {
          ...recievedFlow.__root__,
          data: {
            ...recievedFlow.__root__.data,
            webhookUrl:
              recievedFlow.__root__.data.webhookUrl || buildWebhookUrl(String(recievedFlow.__root__.data.webhookToken)),
          },
        };
      }

      this.flow = {
        ...recievedFlow,
        __root__: {
          ...recievedFlow.__root__,
          triggerType: recievedFlow.__root__.type as TriggerType,
          data: {
            ...recievedFlow.__root__.data,
            triggerType: recievedFlow.__root__.type as TriggerType,
          },
          type: 'Trigger',
        },
      };

      // Normalize data for UI-only keys (backward compatibility with older saved workflows).
      // Example: AskQuestion now stores two shapes under two keys, but backend payload uses `question.options`.
      Object.entries(this.flow).forEach(([id, node]) => {
        if (!node || id === '__root__') {
          return;
        }
        if (node.type === 'AskQuestion') {
          this.flow[id] = normalizeAskQuestionForUI(node as FlowNode);
        } else if (node.type === 'AddGoogleSheetRow') {
          this.flow[id] = normalizeGoogleSheetsForUI(node as FlowNode);
        }
      });

      // Extract variables from the entire flow
      const variablesStore = useWorkflowVariables();
      variablesStore.extractVariablesFromFlow(this.flow);

      // Clear History
      store.resetHistory();
    },

    reset() {
      const store = useWorkflowStore();
      this.flow = {
        __root__: {
          id: '__root__',
          parentId: null,
          name: 'Trigger',
          category: 'Trigger',
          type: 'Trigger',
          children: [],
          data: {
            logicalOperator: 'and',
            triggerType: 'ConversationOpened',
          },
          triggerType: 'ConversationOpened',
        },
      } as Flow;
      // Clear history on reset
      store.resetHistory();
    },

    setDefaultValues(nodeId: string | number) {
      if (this.flow[nodeId]) {
        const stepConfig = stepConfigs[this.flow[nodeId].type];
        if (stepConfig && stepConfig.formFields) {
          this.processFormFieldsRecursively(stepConfig.formFields, this.flow[nodeId].data, nodeId);
        }
      }
    },

    /**
     * Recursively process form fields to set default values
     */
    processFormFieldsRecursively(fields: any[], nodeData: Record<string, any>, nodeId: string | number) {
      fields.forEach((field: any) => {
        // Handle fields with defaultValue
        if ('defaultValue' in field && field.defaultValue !== undefined) {
          const key = field.key;

          // Handle nested keys (e.g., 'question.content', 'saveResponse.hasField')
          if (key.includes('.')) {
            // Split the key by dots and create nested structure
            const keyParts = key.split('.');
            let currentLevel = nodeData;

            // Navigate to the nested level, creating objects as needed
            for (let i = 0; i < keyParts.length - 1; i++) {
              const part = keyParts[i];
              if (!(part in currentLevel)) {
                currentLevel[part] = {};
              }
              currentLevel = currentLevel[part];
            }

            // Set the value at the final level
            const finalKey = keyParts[keyParts.length - 1];
            currentLevel[finalKey] = field.defaultValue;
          } else {
            // Simple key, set directly
            nodeData[key] = field.defaultValue;
          }
        }

        // Handle nested fields (like accordion fields)
        if (field.type === 'accordion' && field.fields && Array.isArray(field.fields)) {
          this.processFormFieldsRecursively(field.fields, nodeData, nodeId);
        }

        // Handle other field types that might have nested fields
        if (field.fields && Array.isArray(field.fields)) {
          this.processFormFieldsRecursively(field.fields, nodeData, nodeId);
        }
      });

      // Update the node data in the flow
      if (this.flow[nodeId]) {
        this.flow[nodeId].data = nodeData;
      }
    },

    /**
     * Export the flow as JSON
     */
    exportFlow() {
      const blob = new Blob([JSON.stringify(this.flow, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'workflow-flow.json';
      a.click();
      URL.revokeObjectURL(url);
    },

    /**
     * Initialize the root node with default values
     */
    initializeRootNode() {
      this.setDefaultValues('__root__');
    },

    /**
     * Get the next step number for a specific step type
     */
    getNextStepNumber(stepType: string): number {
      const existingSteps = Object.values(this.flow).filter(
        (node) => node.category !== 'Connector' && node.type === stepType,
      );
      return existingSteps.length + 1;
    },

    /**
     * Get all available nodes for selection (excluding connectors and jump steps)
     */
    getAvailableNodesForSelection(nodeId: string) {
      const availableNodes: Array<{ label: string; value: string }> = [];

      // Iterate through all nodes in the flow
      Object.values(this.flow).forEach((node) => {
        // Skip the current node
        if (node.id === nodeId) {
          return;
        }

        // Skip connector nodes
        if (node.category === 'Connector') {
          return;
        }

        // Skip jump steps
        if (node.type === 'Jump') {
          return;
        }

        // Skip trigger nodes
        if (node.category === 'Trigger') {
          return;
        }

        // Add this node to available nodes
        availableNodes.push({
          label: node.name,
          value: node.id as string,
        });
      });

      return availableNodes;
    },

    /**
     * Clear references in Jump steps that point to a deleted node
     */
    clearJumpReferences(deletedNodeId: string | number) {
      Object.values(this.flow).forEach((flowNode) => {
        if (flowNode.type === 'Jump' && flowNode.data?.nodeId === deletedNodeId) {
          flowNode.data = {
            ...flowNode.data,
            nodeId: null,
          };
        }
      });
    },
  },
});
